import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  Signal,
} from '@angular/core';
import {
  CONFIGURATION_SETTING_KEYS,
  EMessageType,
  ISubscriptionTicket,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { ConfigurationStore } from '../configuration/configuration-store.service';
import { SubscriptionGateway } from './subscription.gateway';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionTicketsStore {
  private readonly gateway: SubscriptionGateway = new SubscriptionGateway();
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly serviceAreaId: Signal<number> = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.SubscriptionServiceArea
    )
  );

  #subscriptionResource: ResourceRef<
    IApiResponse<ISubscriptionTicket[]> | undefined
  > = resource({
    params: this.serviceAreaId,
    loader: ({ params }: ResourceLoaderParams<number>) => {
      return params != undefined
        ? firstValueFrom(this.gateway.list(params))
        : Promise.resolve(undefined);
    },
  });

  readonly hasLoaded = computed(() => this.#subscriptionResource.hasValue());
  readonly hasError = computed(
    () =>
      !!this.#subscriptionResource.error() ||
      (this.#subscriptionResource.hasValue() &&
        this.#subscriptionResource.value()?.message?.type ===
          EMessageType.ERROR)
  );

  readonly subscriptions: Signal<ISubscriptionTicket[]> = computed(
    (): ISubscriptionTicket[] =>
      this.#subscriptionResource.hasValue()
        ? (this.#subscriptionResource.value()?.data ?? [])
        : []
  );

  readonly isLoading: Signal<boolean> = computed(() =>
    this.#subscriptionResource.isLoading()
  );

  public reloadSubscriptionTickets(): void {
    this.#subscriptionResource.reload();
  }
}
